package com.example.mystybag.appCore.logic.addMerchantLocation

import com.example.mystybag.appCore.model.addMerchantLocation.MerchantRegistrationReviewStoreDetailsUIState
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.common.resources.SR
import com.example.mystybag.core.expected.getLocateFromAddress
import com.example.mystybag.core.expected.getAddressFromLocation
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.core.logic.base.ParentViewModel
import com.example.mystybag.login.logic.NavigatorMerchantRegistrationStep7
import com.example.mystybag.login.logic.NavigatorMerchantRegistrationStep8
import com.example.mystybag.login.logic.NavigatorMerchantRegistrationStep9
import kotlinx.coroutines.launch

class MerchantRegistrationReviewStoreDetailsViewModel(
    context: AppContext,
    private val config: NavigatorMerchantRegistrationStep8
) : ChildViewModel<MerchantRegistrationReviewStoreDetailsUIState>(context, MerchantRegistrationReviewStoreDetailsUIState()) {

    init {
        val lat = config.initialLatitude ?: config.latitude
        val lng = config.initialLongitude ?: config.longitude
        updateState {
            it.copy(
                storeName = config.storeName,
                storeAddress = config.storeAddress,
                cityId = config.cityId,
                cityName = config.cityName,
                countryId = config.countryId,
                countryName = config.countryName,
                storePhoneNumber = config.storePhoneNumber,
                dialCode = config.dialCode,
                latitude = lat,
                longitude = lng
            )
        }
    }

    override fun onCreate() {
        navControls.backButtonVisible = true
        navControls.title = sharedString(SR.strings.confirm_location)
        val lat = config.initialLatitude ?: config.latitude
        val lng = config.initialLongitude ?: config.longitude
        updateState {
            it.copy(
                storeName = config.storeName,
                storeAddress = config.storeAddress,
                cityId = config.cityId,
                cityName = config.cityName,
                countryId = config.countryId,
                countryName = config.countryName,
                storePhoneNumber = config.storePhoneNumber,
                dialCode = config.dialCode,
                latitude = lat,
                longitude = lng
            )
        }
        loadLocationFromAddress()
    }

    private fun loadLocationFromAddress() {
        if (config.initialLatitude != null && config.initialLongitude != null) {
            updateState {
                it.copy(
                    latitude = config.initialLatitude,
                    longitude = config.initialLongitude,
                    isLoading = false
                )
            }
            return
        }
        scope.launch {
            updateState { it.copy(isLoading = true) }
            
            val fullAddress = "${config.storeAddress}, ${config.cityName}"
            val coordinates = getLocateFromAddress(fullAddress)
            
            if (coordinates != null) {
                updateState {
                    it.copy(
                        latitude = coordinates.first,
                        longitude = coordinates.second,
                        isLoading = false
                    )
                }
            } else {
                val cityCoordinates = getLocateFromAddress(config.cityName)
                if (cityCoordinates != null) {
                    updateState {
                        it.copy(
                            latitude = cityCoordinates.first,
                            longitude = cityCoordinates.second,
                            isLoading = false
                        )
                    }
                } else {
                    updateState {
                        it.copy(
                            latitude = config.latitude,
                            longitude = config.longitude,
                            isLoading = false
                        )
                    }
                }
            }
        }
    }

    fun updateLocation(lat: Double, lng: Double) {
        updateState {
            it.copy(
                latitude = lat,
                longitude = lng
            )
        }
    }
    
    fun onMapReady() {
        updateState { it.copy(isMapReady = true) }
    }


    override fun onBackButtonClicked() {
        val state = uiState.value
        
        // Save current state to config before going back
        // Update current config
        config.storeName = state.storeName
        config.storeAddress = state.storeAddress
        config.cityId = state.cityId ?: config.cityId
        config.cityName = state.cityName
        config.countryId = state.countryId
        config.countryName = state.countryName
        config.storePhoneNumber = state.storePhoneNumber
        config.dialCode = state.dialCode
        
        // Also update the previous NavigatorMerchantRegistrationStep7 in the stack
        // by accessing the parent's childStack
        val parent = parentCallbacks as? ParentViewModel
        parent?.childStack?.value?.backStack?.lastOrNull()?.configuration?.let { prevConfig ->
            if (prevConfig is NavigatorMerchantRegistrationStep7) {
                prevConfig.storeName = state.storeName
                prevConfig.storeAddress = state.storeAddress
                prevConfig.cityId = state.cityId ?: prevConfig.cityId
                prevConfig.cityName = state.cityName
                prevConfig.countryId = state.countryId
                prevConfig.countryName = state.countryName
                prevConfig.storePhoneNumber = state.storePhoneNumber
                prevConfig.dialCode = state.dialCode
                prevConfig.packaging = config.packaging
                prevConfig.additionalPickupInstructions = config.additionalPickupInstructions
                prevConfig.surpriseBagContainsBuffetFood = config.surpriseBagContainsBuffetFood
            }
        }
        
        super.onBackButtonClicked()
    }

    fun onContinue() {
        val state = uiState.value
        if (state.latitude != null && state.longitude != null) {
            scope.launch {
                updateState { it.copy(isLoading = true) }
                
                val address = getAddressFromLocation(state.latitude!!, state.longitude!!)
                val updatedStoreAddress = address ?: state.storeAddress
                
                launchOnMain {
                    updateState { it.copy(isLoading = false) }
                    
                    navigate(
                        NavigatorMerchantRegistrationStep9(
                            merchantId = config.merchantId,
                            storeName = state.storeName,
                            storeAddress = updatedStoreAddress,
                            cityId = state.cityId ?: config.cityId,
                            cityName = state.cityName,
                            countryId = state.countryId,
                            countryName = state.countryName,
                            storePhoneNumber = state.storePhoneNumber,
                            dialCode = state.dialCode,
                            packaging = config.packaging,
                            additionalPickupInstructions = config.additionalPickupInstructions,
                            surpriseBagContainsBuffetFood = config.surpriseBagContainsBuffetFood,
                            latitude = state.latitude!!,
                            longitude = state.longitude!!,
                            returnToDashboard = config.returnToDashboard,
                            locationId = config.locationId,
                            whatYouCouldGet = config.whatYouCouldGet,
                            businessCategoryId = config.businessCategoryId,
                            existingCoverPhotoUrl = config.existingCoverPhotoUrl,
                            existingMenuPhotoUrls = config.existingMenuPhotoUrls
                        )
                    )
                }
            }
        }
    }
}

